import React, { useEffect, useState } from 'react';
import { apiErrorText } from '../../lib/apiError';
import { createPortal } from 'react-dom';
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Loader2, Mail, MessageSquare, Phone, MousePointerClick, SendHorizonal, Eye, Sparkles, Info, Check, X } from 'lucide-react';
import apiV2 from '../../utils/apiV2';
import { toast } from 'sonner';

const CH_ICON = { email: Mail, sms: MessageSquare, whatsapp: Phone };

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload || {};
  return (
    <div className="bg-white rounded-xl shadow-lg ring-1 ring-slate-900/[0.08] px-3.5 py-2.5 text-xs">
      <div className="font-bold text-slate-900 mb-1">{new Date(label).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
      <div className="text-slate-500">Sent <span className="font-extrabold text-[#93C5FD]">{d.sent}</span></div>
      <div className="text-slate-500">Views <span className="font-extrabold text-[#60A5FA]">{d.views}</span></div>
      <div className="text-slate-500">Clicks <span className="font-extrabold text-[#2563EB]">{d.clicks}</span></div>
      {d.bookings > 0 && <div className="text-slate-500">Bookings <span className="font-extrabold text-emerald-600">{d.bookings}</span></div>}
    </div>
  );
};

export const CampaignAnalytics = ({ campaignId, orgId, formatMoney, channels = ['email'] }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testChannel, setTestChannel] = useState(channels[0] || 'email');
  const [testTo, setTestTo] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testSentInfo, setTestSentInfo] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let alive = true;
    apiV2.get(`/organizations/${orgId}/marketing/campaigns/${campaignId}/analytics`)
      .then((res) => alive && setData(res.data))
      .catch(() => toast.error('Could not load analytics'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [campaignId, orgId]);

  const openPreview = async (v) => {
    setPreview({ loading: true, title: v.subject || v.angle || 'Ad preview' });
    try {
      const res = await apiV2.get(`/organizations/${orgId}/marketing/campaigns/${campaignId}/variants/${v.id}/preview`);
      setPreview({ loading: false, title: res.data.subject, html: res.data.html });
    } catch {
      toast.error('Could not load ad preview');
      setPreview(null);
    }
  };

  const sendTest = async () => {
    const isEmail = testChannel === 'email';
    if (isEmail && !testTo.includes('@')) { toast.error('Enter a valid email'); return; }
    if (!isEmail && testTo.replace(/\D/g, '').length < 8) { toast.error('Enter a valid phone number'); return; }
    setSendingTest(true);
    try {
      const res = await apiV2.post(`/organizations/${orgId}/marketing/campaigns/${campaignId}/send-test`, {
        channel: testChannel,
        email: isEmail ? testTo : null,
        phone: isEmail ? null : testTo,
      });
      if (isEmail) {
        setTestSentInfo({ count: res.data.count || 1, to: testTo, chargedCents: res.data.chargedCents || 0 });
      } else {
        toast.success(`Test ${testChannel === 'sms' ? 'SMS' : 'WhatsApp message'} sent to ${testTo}${res.data.chargedCents ? ` · ${formatMoney(res.data.chargedCents / 100)} charged` : ''}`);
      }
    } catch (e) {
      toast.error(apiErrorText(e, 'Test send failed'));
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;
  }
  if (!data) return null;

  const k = data.kpis;
  const hasVolume = data.daily.some((d) => d.sent > 0 || d.clicks > 0);
  const opt = data.optimizer || {};
  const optLabel = opt.status !== 'active'
    ? 'Smart delivery · arms at launch'
    : (opt.learning ? 'Smart delivery · learning' : 'Smart delivery · optimizing');

  return (
    <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`campaign-analytics-${campaignId}`}>
      {/* KPI strip */}
      <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4">
        {[
          { label: 'Delivered', value: k.delivered.toLocaleString() },
          { label: 'Views', value: k.views.toLocaleString() },
          { label: 'Clicks', value: k.clicks.toLocaleString() },
          { label: 'CTR', value: `${k.ctr}%` },
          { label: 'Spend', value: formatMoney(k.costCents / 100), sub: k.testCostCents > 0 ? `+ ${formatMoney(k.testCostCents / 100)} on test sends` : null },
        ].map((m) => (
          <div key={m.label}>
            <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">{m.label}</div>
            <div className={`text-sm font-extrabold ${m.hl ? 'text-emerald-600' : 'text-slate-800'}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{m.value}</div>
            {m.sub && <div className="text-[9px] font-semibold text-slate-400">{m.sub}</div>}
          </div>
        ))}
      </div>

      {/* Daily performance chart */}
      <div className="rounded-xl bg-slate-50/70 border border-slate-100 p-3 mb-4" data-testid={`campaign-chart-${campaignId}`}>
        <div className="flex items-center gap-4 px-1 mb-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Last 30 days</span>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-slate-500"><span className="w-2.5 h-2.5 rounded-sm bg-[#BFDBFE]" /> Sent</span>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-slate-500"><span className="w-2.5 h-0.5 rounded bg-[#60A5FA]" /> Views</span>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-slate-500"><span className="w-2.5 h-0.5 rounded bg-[#2563EB]" /> Clicks</span>
        </div>
        {hasVolume ? (
          <ResponsiveContainer width="100%" height={160}>
            <ComposedChart data={data.daily} margin={{ top: 8, right: 4, left: -26, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#E2E8F0" strokeDasharray="3 3" />
              <XAxis
                dataKey="date" tickLine={false} axisLine={false} interval={6}
                tick={{ fontSize: 10, fill: '#94A3B8', fontWeight: 600 }}
                tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}
              />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#94A3B8', fontWeight: 600 }} />
              <Tooltip content={<ChartTip />} cursor={{ stroke: '#CBD5E1' }} />
              <Area type="monotone" dataKey="sent" fill="#BFDBFE" stroke="#93C5FD" strokeWidth={1.5} fillOpacity={0.55} />
              <Line type="monotone" dataKey="views" stroke="#60A5FA" strokeWidth={2} strokeDasharray="4 3" dot={false} activeDot={{ r: 3.5 }} />
              <Line type="monotone" dataKey="clicks" stroke="#2563EB" strokeWidth={2.25} dot={false} activeDot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[160px] flex items-center justify-center text-xs text-slate-400 font-semibold">No sends yet — email yourself a test and watch the analytics light up.</div>
        )}
      </div>

      {/* Per-ad performance — ranked by the optimizer's delivery weighting */}
      {(data.variants || []).length >= 1 && (
        <div className="rounded-xl bg-slate-50/70 border border-slate-100 p-3 mb-4" data-testid={`campaign-variant-stats-${campaignId}`}>
          <div className="flex items-center justify-between px-1 mb-2 gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Ad performance</span>
            {opt.enabled && (
              <span className="relative group inline-flex items-center gap-1.5 cursor-help" data-testid={`campaign-optimizer-note-${campaignId}`}>
                <Sparkles className="w-3 h-3 text-[#2563EB]" />
                <span className="text-[10px] font-bold text-[#2563EB]">{optLabel}</span>
                <Info className="w-3 h-3 text-slate-300" />
                <span className="pointer-events-none absolute right-0 top-full mt-1.5 z-30 hidden group-hover:block w-64 rounded-xl bg-slate-900 text-white text-[11px] font-medium leading-relaxed px-3.5 py-2.5 shadow-xl">
                  Delivery gradually leans toward the ads your audience clicks most — clicks count 3× more than views. Every ad always stays in rotation (never 100% or 0% of sends) and rankings shift as new engagement comes in. Test clicks count as early signal.
                </span>
              </span>
            )}
          </div>
          <div className="space-y-1.5">
            {data.variants.map((v, i) => (
              <div key={v.id} className="flex items-center gap-3 bg-white rounded-lg px-3 py-2" data-testid={`campaign-variant-stat-${campaignId}-${i}`}>
                <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-extrabold ${i === 0 ? 'bg-blue-50 text-[#2563EB] ring-1 ring-blue-100' : 'bg-slate-100 text-slate-400'}`}
                  data-testid={`campaign-variant-rank-${campaignId}-${i}`}>
                  #{v.rank || i + 1}
                </span>
                {v.heroImageUrl
                  ? <img src={v.heroImageUrl} alt="" className="w-9 h-9 rounded-md object-cover shrink-0" />
                  : <div className="w-9 h-9 rounded-md bg-slate-100 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-semibold text-slate-800 truncate">{v.subject || v.angle || `Ad ${i + 1}`}</div>
                  <div className="text-[10px] text-slate-400 font-semibold">
                    {v.angle && <span className="font-bold uppercase tracking-wider mr-2">{v.angle}</span>}
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v.sent} sent · {v.views} views · {v.clicks} clicks</span>
                  </div>
                </div>
                {typeof v.weightPct === 'number' && (
                  <div className="w-24 shrink-0 hidden sm:block" data-testid={`campaign-variant-weight-${campaignId}-${i}`} title="Share of delivery — how often the optimizer sends this ad right now">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-[8px] font-bold uppercase tracking-wider text-slate-400">Delivery</span>
                      <span className={`text-[10px] font-extrabold ${i === 0 ? 'text-[#2563EB]' : 'text-slate-500'}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{v.weightPct}%</span>
                    </div>
                    <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full rounded-full ${i === 0 ? 'bg-[#2563EB]' : 'bg-slate-300'}`} style={{ width: `${Math.min(100, v.weightPct)}%` }} />
                    </div>
                  </div>
                )}
                <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-extrabold ${i === 0 && v.ctr > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {v.ctr}% CTR
                </span>
                <button onClick={() => openPreview(v)} data-testid={`campaign-variant-preview-btn-${campaignId}-${i}`}
                  title="Preview this ad"
                  className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full border border-slate-200 text-[10px] font-bold text-slate-500 hover:text-[#2563EB] hover:border-[#2563EB]/40 transition-colors">
                  <Eye className="w-3 h-3" /> Preview
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Channel breakdown + send test */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-2">
          {data.channels.map((c) => {
            const Icon = CH_ICON[c.channel] || Mail;
            return (
              <span key={c.channel} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 text-[11px] font-bold text-slate-600" data-testid={`campaign-channel-stat-${c.channel}`}>
                <Icon className="w-3.5 h-3.5 text-[#2563EB]" />
                {c.sent} sent · {c.views} views · {c.clicks} clicks · {formatMoney(c.costCents / 100)}
              </span>
            );
          })}
          {data.channels.length === 0 && <span className="text-xs text-slate-400 font-semibold">No channel data yet.</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {channels.length > 1 && (
            <select
              value={testChannel}
              onChange={(e) => { setTestChannel(e.target.value); setTestTo(''); }}
              data-testid={`campaign-test-channel-${campaignId}`}
              className="px-2.5 py-2 rounded-full border border-slate-200 text-xs font-bold text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30"
            >
              {channels.map((ch) => (
                <option key={ch} value={ch}>{ch === 'email' ? 'Email' : ch === 'sms' ? 'SMS' : 'WhatsApp'}</option>
              ))}
            </select>
          )}
          <input
            value={testTo} onChange={(e) => setTestTo(e.target.value)}
            type={testChannel === 'email' ? 'email' : 'tel'}
            placeholder={testChannel === 'email' ? 'you@company.com' : '+1 555 123 4567'}
            data-testid={`campaign-test-email-input-${campaignId}`}
            className="w-44 px-3 py-2 rounded-full border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30"
          />
          <button onClick={sendTest} disabled={sendingTest} data-testid={`campaign-send-test-btn-${campaignId}`}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#2563EB] text-white text-xs font-bold hover:bg-[#1D4ED8] disabled:opacity-50">
            {sendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <SendHorizonal className="w-3.5 h-3.5" />}
            Send test
          </button>
        </div>
      </div>

      {/* Activity feed */}
      {data.activity.length > 0 && (
        <div className="overflow-x-auto" data-testid={`campaign-activity-${campaignId}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[9px] font-bold uppercase tracking-wider text-slate-400">
                <th className="pb-2 pr-2">Contact</th>
                <th className="pb-2 px-2">Channel</th>
                <th className="pb-2 px-2">Sent</th>
                <th className="pb-2 px-2">Viewed</th>
                <th className="pb-2 px-2">Clicked</th>
              </tr>
            </thead>
            <tbody>
              {data.activity.map((a, i) => {
                const Icon = CH_ICON[a.channel] || Mail;
                return (
                  <tr key={i} className="border-t border-slate-50">
                    <td className="py-2 pr-2">
                      <div className="font-semibold text-slate-800 text-[12px]">
                        {a.name}
                        {a.isTest && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-blue-50 text-[#2563EB] text-[9px] font-extrabold uppercase tracking-wider ring-1 ring-blue-100">Test</span>}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono">{a.contact}</div>
                    </td>
                    <td className="py-2 px-2"><Icon className="w-3.5 h-3.5 text-slate-400" /></td>
                    <td className="py-2 px-2 text-[11px] text-slate-500">{a.sentAt ? new Date(a.sentAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}</td>
                    <td className="py-2 px-2">
                      {a.viewed
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 text-[10px] font-bold"><Eye className="w-3 h-3" /> Yes</span>
                        : <span className="text-xs text-slate-300">—</span>}
                    </td>
                    <td className="py-2 px-2">
                      {a.clicked
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-[#2563EB] text-[10px] font-bold"><MousePointerClick className="w-3 h-3" /> Yes</span>
                        : <span className="text-xs text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Ad email preview modal — portaled to <body> so it centers on the viewport */}
      {preview && createPortal(
        <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ad-preview-modal" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: '85vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-slate-100">
              <div className="min-w-0">
                <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">Static preview — buttons are disabled here; live sends track clicks as usual</div>
                <div className="text-sm font-extrabold text-slate-900 truncate">{preview.title}</div>
              </div>
              <button onClick={() => setPreview(null)} data-testid="ad-preview-close"
                className="shrink-0 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            {preview.loading ? (
              <div className="flex items-center justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
            ) : (
              <iframe title="Ad preview" srcDoc={preview.html} sandbox="" className="w-full flex-1 bg-slate-50" style={{ minHeight: '60vh', border: 'none' }} />
            )}
          </div>
        </div>,
        document.body
      )}

      {testSentInfo && createPortal(
        <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="campaign-test-sent-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <div className="w-11 h-11 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
              <Check className="w-5 h-5 text-emerald-600" />
            </div>
            <h3 className="text-lg font-extrabold text-slate-900">Test sent — check your inbox</h3>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              We emailed <span className="font-semibold text-slate-700">{testSentInfo.count} ad variation{testSentInfo.count === 1 ? '' : 's'}</span> to {testSentInfo.to} so you can review the rotation — subjects land exactly as customers see them.
              {testSentInfo.chargedCents > 0 && <> <span className="font-semibold text-slate-700">{formatMoney(testSentInfo.chargedCents / 100)}</span> was deducted from your credits — test sends are billed like real sends.</>}
            </p>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              Your audience never gets them all at once — each contact receives at most one email every {data?.frequencyDays || 7} days, daily sends stay within your budget, and every touch rotates to a fresh variation to keep converting. Opens and clicks on these test emails show up in the activity feed below, so you can watch the tracking work.
            </p>
            <button onClick={() => setTestSentInfo(null)} data-testid="campaign-test-sent-close"
              className="mt-4 w-full py-2.5 rounded-full bg-slate-900 text-white text-sm font-bold hover:bg-slate-800">Got it</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
